import { window as $window } from "global";

type Assignment = {
    _bound_unload: Function;
    id: string;
    questions: Question[];
};

type Box = {
    _getBoxValue: Function;
};

type ButtonStates = {
    gradeThisBttn: boolean;
    hideAnswerBttn: boolean;
    showAnswerBttn: boolean;
    showSolutionBttn: boolean;
    tryAgainBttn: boolean;
};

type Question = {
    boxes: Box[];
    id: string;
    position: number;
};

type QuestionResponse = {
    box: number;
    response: string;
};

type QuestionResponses = {
    areEmpty?: boolean;
    id: string;
    position: number;
    responses?: QuestionResponse[];
};

export class PracticeAnotherVersion {
    assignment: Assignment;
    gradeThisBttn: HTMLElement | null = null;
    hideAnswerBttn: HTMLElement | null = null;
    reloadInput: HTMLInputElement | null = null;
    responsesInput: HTMLInputElement | null = null;
    showAnswerBttn: HTMLElement | null = null;
    showSolutionBttn: HTMLElement | null = null;
    tryAgainBttn: HTMLElement | null = null;

    constructor(assignment: Assignment) {
        this.assignment = assignment;
    }

    private gatherResponses() {
        // the current Assignment and Question Submittable JavaScripts have an assumption about gathering responses that is incompatible
        // with PAV, so we manually gather the responses here
        const question = this.assignment.questions[0];
        const response: QuestionResponses = {
            id: question.id,
            position: question.position,
        };

        let responses: QuestionResponse[] = [];
        let areResponsesEmpty = true;
        for (let i = 0; i < question.boxes.length; i++) {
            const response = question.boxes[i]._getBoxValue();
            responses.push({
                box: i,
                response: response,
            });
            // an empty response will look like '1=' or 'c='
            const parts = response.split("=");
            if (parts[1].length) {
                areResponsesEmpty = false;
            }
        }
        response.responses = responses;
        response.areEmpty = areResponsesEmpty;

        return response;
    }

    getButtonStates() {
        let states: ButtonStates = {
            gradeThisBttn: false,
            hideAnswerBttn: false,
            showAnswerBttn: false,
            showSolutionBttn: false,
            tryAgainBttn: false,
        };

        if (this.gradeThisBttn) {
            states.gradeThisBttn = true;
        }

        if (this.showAnswerBttn) {
            states.showAnswerBttn = true;
        }

        if (this.hideAnswerBttn) {
            states.hideAnswerBttn = true;
        }

        if (this.showSolutionBttn) {
            states.showSolutionBttn = true;
        }

        if (this.tryAgainBttn) {
            states.tryAgainBttn = true;
        }

        return states;
    }

    private handleGradeThisBttnClick() {
        const responses = this.gatherResponses();

        if (this.responsesInput) {
            if (responses.areEmpty) {
                this.responsesInput.value = $window.JSON.stringify({});
                const reloadInput = $window.document.getElementById("reload");
                if (reloadInput instanceof HTMLInputElement) {
                    reloadInput.value = "true";
                }
            } else {
                this.responsesInput.value = $window.JSON.stringify(responses);
            }
        }

        this.submitForm();
    }

    private handleShowAnswerBttnClick() {
        const showKeyInput = $window.document.getElementById("showKey");
        if (showKeyInput instanceof HTMLInputElement) {
            showKeyInput.value = "true";
        }
        const responses = this.gatherResponses();
        if (this.responsesInput) {
            this.responsesInput.value = $window.JSON.stringify(responses);
        }

        this.submitForm();
    }

    private handleHideAnswerBttnClick() {
        const showKeyInput = $window.document.getElementById("showKey");
        if (showKeyInput instanceof HTMLInputElement) {
            showKeyInput.value = "false";
        }
        const responses = this.gatherResponses();
        if (this.responsesInput) {
            this.responsesInput.value = $window.JSON.stringify(responses);
        }
        this.submitForm();
    }

    private handleShowSolutionBttnClick() {
        const showSolutionInput =
            $window.document.getElementById("showSolution");
        if (showSolutionInput instanceof HTMLInputElement) {
            showSolutionInput.value = "true";
        }

        this.submitForm();
    }

    private handleTryAgainBttnClick() {
        const tryAgainInput = $window.document.getElementById("tryAgain");
        if (tryAgainInput instanceof HTMLInputElement) {
            tryAgainInput.value = "true";
        }

        this.submitForm();
    }

    init() {
        this.gradeThisBttn = $window.document.getElementById("gradeThisBttn");
        this.showAnswerBttn = $window.document.getElementById("showAnswerBttn");
        this.hideAnswerBttn = $window.document.getElementById("hideAnswerBttn");
        this.showSolutionBttn =
            $window.document.getElementById("showSolutionBttn");
        this.tryAgainBttn = $window.document.getElementById("tryAgainBttn");

        this.gradeThisBttn?.addEventListener("click", () =>
            this.handleGradeThisBttnClick(),
        );

        this.showAnswerBttn?.addEventListener("click", () =>
            this.handleShowAnswerBttnClick(),
        );

        this.hideAnswerBttn?.addEventListener("click", () =>
            this.handleHideAnswerBttnClick(),
        );

        this.showSolutionBttn?.addEventListener("click", () =>
            this.handleShowSolutionBttnClick(),
        );

        this.tryAgainBttn?.addEventListener("click", () =>
            this.handleTryAgainBttnClick(),
        );

        const responsesInput = $window.document.getElementById("responses");
        if (responsesInput instanceof HTMLInputElement) {
            this.responsesInput = responsesInput;
        }
    }

    submitForm() {
        // kill the onBeforeUnload event so we don't warn the user about leaving the page
        $window.waUtil.removeUnloadEvent(this.assignment._bound_unload);

        const form = $window.document.getElementById("pav_form");
        if (form instanceof HTMLFormElement) {
            form.submit();
        }
    }
}
